import axios, { AxiosError } from "axios";
import useSWR from "swr";
import { useContext, useMemo } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { ILiveTrade } from "../../../interfaces/liveTrade";

interface IUseLiveTradesParams {
  instrumentTypes: string[];
  minNotional?: number;
  includeLiquidations: boolean;
  asset?: string;
  lookbackMinutes?: number;
}

const getLiveTradesEndpoint = () => {
  const baseUrl =
    process.env.REACT_APP_TRADES_API_URL ??
    process.env.REACT_APP_AEVO_API_URL_MAINNET ??
    (typeof window !== "undefined" ? window.location.origin : "");

  const normalizedBase = baseUrl.endsWith("/")
    ? baseUrl.slice(0, -1)
    : baseUrl;

  return `${normalizedBase}/api/trades/live-trades`;
};

const deriveAsset = (trade: ILiveTrade): string | undefined => {
  if (trade.asset && trade.asset.length) {
    return trade.asset.toUpperCase();
  }

  if (trade.instrument_name) {
    const [asset] = trade.instrument_name.split("-");
    if (asset) {
      return asset.toUpperCase();
    }
  }

  return undefined;
};

const normalizeTrades = (trades: ILiveTrade[]): ILiveTrade[] =>
  trades.map((trade) => {
    let makerAccounts: string[] = [];

    if (Array.isArray(trade.maker_accounts)) {
      makerAccounts = trade.maker_accounts;
    } else if (Array.isArray(trade.maker)) {
      makerAccounts = trade.maker;
    }

    return {
      ...trade,
      agg_order_id: trade.agg_order_id ?? trade.aggressing_order_id,
      taker: trade.taker ?? trade.taker_account,
      maker_accounts: Array.isArray(trade.maker_accounts)
        ? trade.maker_accounts
        : makerAccounts,
      maker: makerAccounts,
      timestamp: trade.timestamp ?? trade.created_timestamp,
      asset: deriveAsset(trade),
      maker_fills: Array.isArray(trade.maker_fills) ? trade.maker_fills : [],
    };
  });

export const useLiveTrades = ({
  instrumentTypes,
  minNotional,
  includeLiquidations,
  asset,
  lookbackMinutes = 60 * 24, // Default to 24 hours
}: IUseLiveTradesParams) => {
  const { tokenId } = useContext(AuthContext);

  const instrumentTypeKey = useMemo(
    () => instrumentTypes.slice().sort().join(","),
    [instrumentTypes]
  );
  const endpoint = useMemo(() => getLiveTradesEndpoint(), []);

  const params = useMemo(() => {
    const query: Record<string, string | number> = {
      include_liquidations: includeLiquidations ? "true" : "false",
    };

    if (instrumentTypeKey.length) {
      query.instrument_types = instrumentTypeKey;
    }

    if (typeof minNotional === "number" && minNotional > 0) {
      query.min_notional = minNotional;
    }

    if (asset) {
      query.assets = asset;
    }

    if (typeof lookbackMinutes === "number" && lookbackMinutes > 0) {
      query.lookback_minutes = lookbackMinutes;
    }

    return query;
  }, [asset, includeLiquidations, instrumentTypeKey, minNotional, lookbackMinutes]);

  const shouldFetch =
    Boolean(tokenId) && (instrumentTypes.length > 0 || includeLiquidations);

  const swr = useSWR<ILiveTrade[], AxiosError>(
    shouldFetch
      ? [
        endpoint,
        tokenId,
        instrumentTypeKey,
        typeof minNotional === "number" ? minNotional : "none",
        includeLiquidations,
        asset ?? "",
        lookbackMinutes,
      ]
      : null,
    async () => {
      const response = await axios.get(endpoint, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${tokenId}`,
          "Access-Control-Allow-Origin": "*",
        },
        params,
      });

      if (Array.isArray(response.data)) {
        return normalizeTrades(response.data as ILiveTrade[]);
      }

      if (Array.isArray(response.data?.data)) {
        return normalizeTrades(response.data.data as ILiveTrade[]);
      }

      return [];
    },
    {
      refreshInterval: 5000,
    }
  );

  return { ...swr };
};
